'use client';

import { type Product, ProductBrief } from '@/domain/products/types';
import { type TabItem, visibleTabs, when } from '@/features/products/utils/tabs';
import { useMediaQuery } from '@/hooks';
import { CircularProgress } from '@andritzot/metris-web-ui/feedback/progress/circular-progress';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Tab, Tabs } from '@andritzot/metris-web-ui/navigation/tabs';
import { Suspense, useState } from 'react';

import ProductNewsTable from '../ProductNewsTable';
import ProductCard from '../ProductPage/ProductCard';
import {
  BrochureTab,
  BusinessModelTab,
  DocumentsTab,
  Overview,
  PlantsDataTable,
  ProcedureDataTable,
  RoadmapTab,
} from './components';
import { ContactInfoTab, hasContactInfo } from './components/ContactInfoTab/ContactInfoTab';
import { MaturitySectionWrapper } from './components/Overview/components';
import { TabPanels } from './TabPanels';

interface ProductDetailProps {
  detailedProduct: Product;
  isLoggedIn: boolean;
}

const sxProductList = {
  display: 'flex',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  width: '100%',
  mb: '15px',
  ml: '10px',
};

const suspenseFallback = <CircularProgress />;

const renderProductList = (products: ProductBrief[]) => (
  <Box sx={sxProductList}>
    {products.map((related) => (
      <ProductCard key={related.idProduct} product={related} layout="small" redirectToProductPage />
    ))}
  </Box>
);


const TAB_CONTENT_PADDING = 4;
const MOBILE_TAB_CONTENT_PADDING = 2;

function buildTabs(product: Product, isLoggedIn: boolean, contentPadding: number): TabItem[] {
  return visibleTabs([
    {
      label: 'OVERVIEW',
      content: <Overview product={product} isLoggedIn={isLoggedIn} bleed={contentPadding} />,
    },
    when(product.relatedAddons?.length, {
      label: 'ADD-ONS',
      content: renderProductList(product.relatedAddons),
    }),
    when(product.relatedProducts?.length, {
      label: 'RELATED SOLUTIONS',
      content: renderProductList(product.relatedProducts),
    }),
    when(hasContactInfo(product, isLoggedIn), {
      label: 'CONTACT INFO',
      content: <ContactInfoTab product={product} isLoggedIn={isLoggedIn} />,
    }),
    when(product.documentation && isLoggedIn, {
      label: 'BROCHURE',
      content: <BrochureTab detailedProduct={product} />,
    }),
    when(product.businessModels && isLoggedIn, {
      label: 'BUSINESS MODELS',
      content: <BusinessModelTab detailedProduct={product} />,
    }),
    when(product.roadmaps?.length && isLoggedIn, { label: 'ROADMAP', content: <RoadmapTab product={product} /> }),
    when(isLoggedIn, {
      label: 'PLANTS',
      content: (
        <Suspense fallback={suspenseFallback}>
          <PlantsDataTable productId={product.idProduct} />
        </Suspense>
      ),
    }),
    when(product.linkYammer, {
      label: 'NEWS FEED',
      content: (
        <iframe
          name="embed-feed"
          title="Yammer"
          src={product.linkYammer}
          style={{ border: 0, overflow: 'hidden', width: '100%', height: '100%', minHeight: '400px' }}
        />
      ),
    }),
    when(product.procedures?.length && isLoggedIn, {
      label: 'PROCEDURES',
      content: <ProcedureDataTable procedures={product.procedures} />,
    }),
    when(product.documents?.length && isLoggedIn, {
      label: 'DOCUMENTS',
      content: <DocumentsTab documents={product.documents!} />,
    }),
    when(product.idMaturityModel && isLoggedIn, {
      label: 'MATURITY',
      content: (
        <Suspense fallback={suspenseFallback}>
          <MaturitySectionWrapper idProduct={product.idProduct} idMaturityModel={product.idMaturityModel!} />
        </Suspense>
      ),
    }),
    when(product.news?.length, {
      label: 'DISCOVER MORE',
      content: <ProductNewsTable news={product.news} readonly />,
    }),
  ]);
}

const ProductDetail = ({ detailedProduct, isLoggedIn }: ProductDetailProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const { isMobile } = useMediaQuery();
  const contentPadding = isMobile ? MOBILE_TAB_CONTENT_PADDING : TAB_CONTENT_PADDING;
  const tabs = buildTabs(detailedProduct, isLoggedIn, contentPadding);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Tabs
        value={activeTab}
        onChange={(_, value: number) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        type="secondary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {tabs.map(({ label }, index) => (
          <Tab key={label} label={label} value={index} />
        ))}
      </Tabs>
      <Box sx={{ p: contentPadding, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <TabPanels tabs={tabs} activeIndex={activeTab} renderAll={!isLoggedIn} />
      </Box>
    </Box>
  );
};

export default ProductDetail;
